import ResponsiveImage from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import SolidPlayIcon from '../Icons/solid/SolidPlayIcon'
import VimeoThumbnail from 'components/Common/Video/VimeoThumbnail'
import IconButton from '../Button/IconButton'
import LineAngleLeftIcon from '../Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from '../Icons/line/LineAngleRightIcon'
import { EmptyArray } from 'lib/array/arrayUtils'

const THUMBNAIL_WIDTH = 80
const THUMBNAIL_HEIGHT = 80
const THUMBNAIL_GAP = 12
const THUMBNAIL_BORDER_WIDTH = 2
const THUMBNAIL_OVERLAY_OPACITY = 0.3
const getThumbnailOverlayColor = (theme: App.Design.Theme) => theme.palette.neutral.surface.darkBackground
const SCROLL_STEP = (THUMBNAIL_WIDTH + THUMBNAIL_GAP) * 3

const StripRoot = styled.div`
  position: relative;
  min-width: 0;
`

const ThumbnailContainer = styled.div<{ $centered: boolean }>`
  display: flex;
  gap: ${rem(THUMBNAIL_GAP)};
  justify-content: ${props => props.$centered ? 'center' : 'flex-start'};
  overflow-x: auto;
  overflow-y: hidden;
  padding: ${rem(4)} ${rem(16)};
  scrollbar-width: none;
  scroll-behavior: smooth;
  min-width: 0;

  &::-webkit-scrollbar {
    display: none;
  }
`

const NavArrow = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease-in-out;

  ${StripRoot}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
`

const LeftArrow = styled(NavArrow)`
  left: ${rem(8)};
`

const RightArrow = styled(NavArrow)`
  right: ${rem(8)};
`

const ThumbnailButton = styled(Clickable)<{ $isSelected: boolean; $highlightColor?: string }>`
  flex-shrink: 0;
  width: ${rem(THUMBNAIL_WIDTH)};
  height: ${rem(THUMBNAIL_HEIGHT)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  position: relative;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    background-color: ${props => props.$isSelected ? 'transparent' : getThumbnailOverlayColor(props.theme)};
    opacity: ${props => props.$isSelected ? 1 : THUMBNAIL_OVERLAY_OPACITY};
    transition: opacity 0.1s ease-in-out;
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    border: ${THUMBNAIL_BORDER_WIDTH}px solid ${props => props.$isSelected ? (props.$highlightColor ?? props.theme.palette.neutral.surface.normalBackground) : 'transparent'};
    transition: border-color 0.1s ease-in-out;
    pointer-events: none;
    z-index: 2;
  }

  &:hover::before {
    opacity: 0;
  }

  &:hover::after {
    border-color: ${props => props.$highlightColor ?? props.theme.palette.neutral.surface.normalBackground};
  }

  &:focus-visible::after {
    border-color: ${props => props.$highlightColor ?? props.theme.palette.neutral.surface.normalBackground};
    box-shadow: inset 0 0 0 2px ${props => props.$highlightColor ?? props.theme.palette.neutral.surface.normalBackground};
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const PlayIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: ${rem(16)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  z-index: 2;

  svg {
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

interface Props {
  images: Array<App.Image>
  videoIds?: Array<string>
  videoPosition?: number
  selectedIndex: number
  onSelect: (index: number) => void
  className?: string
  showScrollArrows?: boolean
  highlightColor?: string
}

type StripItem =
  | { type: 'image', key: string, image: App.Image }
  | { type: 'video', key: string, videoId: string }

function ImageGalleryThumbnailStrip(props: Props) {
  const { images, selectedIndex, onSelect, className, videoIds = EmptyArray, videoPosition, showScrollArrows = false, highlightColor } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)
  const [canScroll, setCanScroll] = useState(false)
  const intl = useIntl()

  const scrollThumbnailsLeftLabel = intl.formatMessage({
    id: '5p5jmT9_8D1noV_ZYtDx',
    defaultMessage: 'Scroll thumbnails left',
    description: 'Accessibility aria-label for thumbnail strip left scroll button',
  })
  const scrollThumbnailsRightLabel = intl.formatMessage({
    id: 'Uc4EiN_TSun7isl9iyqp',
    defaultMessage: 'Scroll thumbnails right',
    description: 'Aria label for thumbnail strip right scroll button',
  })
  const videoThumbnailLabel = intl.formatMessage({
    id: 'K2ezHVGqrX4rDxM6pWzk',
    defaultMessage: 'Video thumbnail',
    description: 'Alt text for a video thumbnail image in thumbnail strip',
  })
  const getImageFallbackAlt = useCallback((index: number) => {
    return intl.formatMessage(
      {
        id: 'kQX15J_6ShAyEiCC_6Pp',
        defaultMessage: 'Image {index}',
        description: 'Fallback alt text for a gallery thumbnail image when no title is available',
      },
      { index: index + 1 },
    )
  }, [intl])
  const getGoToItemLabel = useCallback((item: StripItem, index: number) => {
    if (item.type === 'video') {
      return intl.formatMessage({
        id: 'AxjrOAUPZ6AIkcryixKg',
        defaultMessage: 'Go to video',
        description: 'Aria label for selecting the video in thumbnail strip',
      })
    }

    return intl.formatMessage(
      {
        id: 'wLaDW1Wama335mX0h1wV',
        defaultMessage: 'Go to image {index}',
        description: 'Aria label for selecting an image in thumbnail strip',
      },
      { index: index + 1 },
    )
  }, [intl])

  const items = useMemo<Array<StripItem>>(() => {
    const baseImages: Array<StripItem> = images.map((image, index) => ({
      type: 'image',
      key: image.id ?? image.url ?? index.toString(),
      image,
    }))

    if (!videoIds.length || videoPosition === undefined) return baseImages

    const insertIndex = Math.max(videoPosition - 1, 0)
    const videoItems: Array<StripItem> = videoIds.map(videoId => ({
      type: 'video',
      key: `video-${videoId}`,
      videoId,
    }))

    return [...baseImages.slice(0, insertIndex), ...videoItems, ...baseImages.slice(insertIndex)]
  }, [images, videoIds, videoPosition])

  // Auto-scroll to keep selected thumbnail visible
  useEffect(() => {
    if (selectedRef.current && containerRef.current) {
      const container = containerRef.current
      const thumbnail = selectedRef.current
      const containerRect = container.getBoundingClientRect()
      const thumbnailRect = thumbnail.getBoundingClientRect()

      const isOutOfViewLeft = thumbnailRect.left < containerRect.left
      const isOutOfViewRight = thumbnailRect.right > containerRect.right

      if (isOutOfViewLeft || isOutOfViewRight) {
        thumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
    }
  }, [selectedIndex])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const updateCanScroll = () => {
      setCanScroll(container.scrollWidth > container.clientWidth)
    }
    updateCanScroll()
    window.addEventListener('resize', updateCanScroll)
    return () => window.removeEventListener('resize', updateCanScroll)
  }, [items])

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const nextIndex = Number(event.currentTarget.dataset.index ?? '0')
    onSelect(nextIndex)
  }, [onSelect])

  const handleLeftClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const container = containerRef.current
    if (!container) return
    container.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' })
  }, [])

  const handleRightClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const container = containerRef.current
    if (!container) return
    container.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' })
  }, [])

  return (
    <StripRoot className={className}>
      {showScrollArrows && canScroll && (
        <>
          <LeftArrow
            kind="secondary"
            variant="dark"
            shape="circle"
            size="small"
            onClick={handleLeftClick}
            aria-label={scrollThumbnailsLeftLabel}
          >
            <LineAngleLeftIcon />
          </LeftArrow>
          <RightArrow
            kind="secondary"
            variant="dark"
            shape="circle"
            size="small"
            onClick={handleRightClick}
            aria-label={scrollThumbnailsRightLabel}
          >
            <LineAngleRightIcon />
          </RightArrow>
        </>
      )}

      <ThumbnailContainer ref={containerRef} $centered={!canScroll}>
        {items.map((item, index) => {
          const isSelected = index === selectedIndex
          return (
            <ThumbnailButton
              key={item.key}
              ref={isSelected ? selectedRef : undefined}
              $isSelected={isSelected}
              $highlightColor={highlightColor}
              onClick={handleClick}
              data-index={index}
              aria-label={getGoToItemLabel(item, index)}
              aria-current={isSelected ? 'true' : undefined}
            >
              {item.type === 'video' && (
                <>
                  <VimeoThumbnail vimeoVideoId={item.videoId} alt={videoThumbnailLabel} />
                  <PlayIconOverlay aria-hidden="true">
                    <SolidPlayIcon />
                  </PlayIconOverlay>
                </>
              )}
              {item.type === 'image' && item.image?.id && (
                <ResponsiveImage
                  id={item.image.id}
                  alt={item.image.title ?? getImageFallbackAlt(index)}
                  quality="eco"
                  fit="center"
                  width={THUMBNAIL_WIDTH}
                  loading="lazy"
                />
              )}
              {item.type === 'image' && !item.image?.id && item.image?.url && (
                <Image
                  image={item.image}
                  alt={item.image.title ?? getImageFallbackAlt(index)}
                  fit="center"
                  loading="lazy"
                />
              )}
            </ThumbnailButton>
          )
        })}
      </ThumbnailContainer>
    </StripRoot>
  )
}

export default React.memo(ImageGalleryThumbnailStrip)
